/**
 * Categorías — mirror del desktop. Layout full-height con tabla,
 * productCount clickeable (filtra /products), edit/delete modals, empty
 * state, permission gating.
 */

import { Button, Card, CardContent } from '@atreus/ui';
import { Link } from '@tanstack/react-router';
import { Lock, Pencil, Plus, Tag, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import { useConfirm } from '../components/ConfirmDialog.js';
import { RowsSkeleton } from '../components/skeletons.js';
import { usePermissions } from '../lib/permissions.js';
import { trpc } from '../lib/trpc.js';

interface Editing {
  id?: string;
  name: string;
}

export const CategoriesPage = () => {
  const categories = trpc.categories.list.useQuery();
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState<Editing | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const permissions = usePermissions();
  const canManage = permissions.canDo('categories.manage');

  const invalidate = () => utils.categories.list.invalidate();
  const create = trpc.categories.create.useMutation({ onSuccess: invalidate });
  const update = trpc.categories.update.useMutation({ onSuccess: invalidate });
  const del = trpc.categories.delete.useMutation({ onSuccess: invalidate });

  const save = () => {
    if (!editing) return;
    if (editing.id) {
      update.mutate(
        { id: editing.id, name: editing.name },
        { onSuccess: () => setEditing(null) },
      );
    } else {
      create.mutate(
        { name: editing.name, active: true },
        { onSuccess: () => setEditing(null) },
      );
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const ok = await confirm({
      title: '¿Eliminar categoría?',
      message: `Se eliminará "${name}". Los productos que la usaban quedarán sin categoría.`,
      confirmLabel: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    del.mutate({ id });
  };

  const count = categories.data?.length ?? 0;

  return (
    <div className="flex h-full flex-col">
      {confirmDialog}
      <header className="flex items-center justify-between border-b border-border bg-surface px-8 py-5">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Categorías</h1>
          <p className="text-sm text-fg-muted">
            {count} {count === 1 ? 'categoría' : 'categorías'}
          </p>
        </div>
        {canManage ? (
          <Button size="lg" onClick={() => setEditing({ name: '' })}>
            <Plus className="size-4" /> Nueva categoría
          </Button>
        ) : (
          <span
            className="flex items-center gap-2 text-xs text-fg-muted"
            title="Solo administradores pueden modificar el catálogo"
          >
            <Lock className="size-3.5" /> Solo lectura
          </span>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-8">
        {del.error ? (
          <Card className="mb-4 border-danger/50 bg-danger/10">
            <CardContent className="p-3 text-sm text-danger">
              {del.error.message}
            </CardContent>
          </Card>
        ) : null}

        {categories.isLoading ? (
          <RowsSkeleton count={8} />
        ) : count === 0 ? (
          <Card className="mx-auto max-w-md">
            <CardContent className="p-8 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-surface-muted text-fg-muted">
                <Tag className="size-5" />
              </div>
              <h2 className="mt-4 text-lg font-bold tracking-tight">
                Aún no hay categorías
              </h2>
              <p className="mt-1 text-sm text-fg-muted">
                Agrupa los productos del menú (bebidas, entradas, platos
                fuertes, etc).
              </p>
              {canManage ? (
                <Button
                  className="mt-4"
                  onClick={() => setEditing({ name: '' })}
                >
                  <Plus className="size-4" /> Crear categoría
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-hidden rounded-md border border-border bg-surface">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-muted text-left text-xs uppercase tracking-wider text-fg-muted">
                <tr>
                  <th className="px-4 py-2 font-semibold">Nombre</th>
                  <th className="w-32 px-4 py-2 text-right font-semibold">
                    Productos
                  </th>
                  {canManage ? <th className="w-32 px-4 py-2"></th> : null}
                </tr>
              </thead>
              <tbody>
                {categories.data?.map((cat) => (
                  <tr
                    key={cat.id}
                    className="border-t border-border transition-colors hover:bg-surface-muted"
                  >
                    <td className="px-4 py-3 font-medium">
                      <Link
                        to="/products"
                        search={{ categoryId: cat.id }}
                        className="hover:underline"
                        title={`Ver productos de "${cat.name}"`}
                      >
                        {cat.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to="/products"
                        search={{ categoryId: cat.id }}
                        className="font-semibold tabular-nums hover:underline"
                      >
                        {cat.productCount}
                      </Link>
                    </td>
                    {canManage ? (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setEditing({ id: cat.id, name: cat.name })
                            }
                            title="Editar"
                          >
                            <Pencil className="size-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(cat.id, cat.name)}
                            disabled={del.isPending || cat.productCount > 0}
                            title={
                              cat.productCount > 0
                                ? 'Reasigna o elimina primero los productos'
                                : 'Eliminar'
                            }
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing !== null ? (
        <CategoryDialog
          editing={editing}
          onChange={setEditing}
          onSave={save}
          onClose={() => setEditing(null)}
          pending={create.isPending || update.isPending}
          error={create.error?.message ?? update.error?.message ?? null}
        />
      ) : null}
    </div>
  );
};

const CategoryDialog = ({
  editing,
  onChange,
  onSave,
  onClose,
  pending,
  error,
}: {
  editing: Editing;
  onChange: (e: Editing) => void;
  onSave: () => void;
  onClose: () => void;
  pending: boolean;
  error: string | null;
}) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
    role="dialog"
    aria-modal="true"
    onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}
  >
    <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
      <header className="flex items-center justify-between border-b border-border px-5 py-3">
        <h2 className="text-lg font-black tracking-tight">
          {editing.id ? 'Editar categoría' : 'Nueva categoría'}
        </h2>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
          <X className="size-4" />
        </Button>
      </header>
      <form
        className="space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (editing.name.trim() && !pending) onSave();
        }}
      >
        <div>
          <label
            htmlFor="category-name"
            className="mb-1 block text-sm font-semibold"
          >
            Nombre
          </label>
          <input
            id="category-name"
            autoFocus
            value={editing.name}
            onChange={(e) => onChange({ ...editing, name: e.target.value })}
            placeholder="Bebidas, Entradas, Platos fuertes…"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!editing.name.trim() || pending}>
            {pending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </form>
    </div>
  </div>
);
